import React from 'react';
import { Select } from './Select';

interface CurrencySelectProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

const CURRENCIES: Record<string, string> = {
  AED: 'UAE Dirham', AUD: 'Australian Dollar',
  BHD: 'Bahraini Dinar', BRL: 'Brazilian Real', CAD: 'Canadian Dollar',
  CHF: 'Swiss Franc', CLP: 'Chilean Peso', CNY: 'Chinese Yuan',
  COP: 'Colombian Peso', CZK: 'Czech Koruna', DKK: 'Danish Krone',
  EGP: 'Egyptian Pound', EUR: 'Euro', GBP: 'British Pound',
  HKD: 'Hong Kong Dollar', HUF: 'Hungarian Forint', IDR: 'Indonesian Rupiah',
  ILS: 'Israeli Shekel', INR: 'Indian Rupee', ISK: 'Icelandic Króna',
  JPY: 'Japanese Yen', KRW: 'South Korean Won', KWD: 'Kuwaiti Dinar',
  MXN: 'Mexican Peso', MYR: 'Malaysian Ringgit', NGN: 'Nigerian Naira',
  NOK: 'Norwegian Krone', NZD: 'New Zealand Dollar', PHP: 'Philippine Peso',
  PKR: 'Pakistani Rupee', PLN: 'Polish Zloty', QAR: 'Qatari Riyal',
  RON: 'Romanian Leu', RUB: 'Russian Ruble', SAR: 'Saudi Riyal',
  SEK: 'Swedish Krona', SGD: 'Singapore Dollar', THB: 'Thai Baht',
  TRY: 'Turkish Lira', TWD: 'New Taiwan Dollar', UAH: 'Ukrainian Hryvnia',
  USD: 'United States Dollar', VND: 'Vietnamese Dong', ZAR: 'South African Rand',
};

export const CurrencySelect: React.FC<CurrencySelectProps> = ({
  label,
  value,
  onChange,
  className
}) => {
  // Detect browser locale currency to prioritise it
  let browserCurrency = '';
  try {
    const region = new Intl.Locale(navigator.language).region;
    const regionMap: Record<string, string> = {
      US: 'USD', GB: 'GBP', AU: 'AUD', CA: 'CAD', JP: 'JPY', CN: 'CNY', IN: 'INR',
      DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR', NL: 'EUR', BE: 'EUR', AT: 'EUR',
      IE: 'EUR', FI: 'EUR', PT: 'EUR', GR: 'EUR', BG: 'EUR', CH: 'CHF', NZ: 'NZD',
      BR: 'BRL', RU: 'RUB', KR: 'KRW', SE: 'SEK', NO: 'NOK', DK: 'DKK', PL: 'PLN',
      SG: 'SGD', HK: 'HKD', MX: 'MXN', ZA: 'ZAR', TH: 'THB', PH: 'PHP',
      MY: 'MYR', ID: 'IDR', TR: 'TRY', UA: 'UAH', EG: 'EGP', NG: 'NGN',
    };
    if (region) browserCurrency = regionMap[region] ?? '';
  } catch { /* ignore */ }

  const priority = ['GBP', 'EUR', 'USD'];
  if (browserCurrency && !priority.includes(browserCurrency)) {
    priority.unshift(browserCurrency);
  } else if (browserCurrency) {
    const idx = priority.indexOf(browserCurrency);
    if (idx > -1) priority.splice(idx, 1);
    priority.unshift(browserCurrency);
  }

  const options = Object.entries(CURRENCIES)
    .map(([code, name]) => ({ value: code, label: `${code} - ${name}` }))
    .sort((a, b) => {
      const ia = priority.indexOf(a.value);
      const ib = priority.indexOf(b.value);
      if (ia !== -1 && ib !== -1) return ia - ib;
      if (ia !== -1) return -1;
      if (ib !== -1) return 1;
      return a.value.localeCompare(b.value);
    });

  return (
    <Select
      {...(label ? { label } : {})}
      value={value}
      onChange={(e) => { onChange(e.target.value); }}
      options={options}
      className={className ?? ''}
    />
  );
};
